// src/routes/RecruitPlanAssistant/RecruitPlanAssistant.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  useMediaQuery,
  useTheme,
  Typography,
  Button,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  Paper,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Container,
  Grid,
  Fab
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import ForumIcon from '@mui/icons-material/Forum';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckIcon from '@mui/icons-material/Check';
import ErrorIcon from '@mui/icons-material/Error';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WorkIcon from '@mui/icons-material/Work';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import HistoryIcon from '@mui/icons-material/History';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';

import axios from 'axios';
import { useAuth } from '../../hooks/useAuth';
import { showError } from '../../utils/errorHandler';
import { segmentedTabsContainer, segmentedTab, chatIconButton, menuIconButton, selectionCard, selectionCardButton, designTokens } from '../../theme/designTokens';
import { bg, text as textColors, border, shadow, adaptiveColor } from '../../utils/darkModeAdapter';

const recruitPlanTheme = designTokens.featureThemes.recruit_plan;

import AppLayout, { useSidebarContext } from '../../components/layout/AppLayout';
import AIChat from '../../components/AIChat';
import AIChatSide from '../../components/AIChatSide';
import RecruitPlanForm from './RecruitPlanForm';
import RequirementsView from './RequirementsView';
import PlanView from './PlanView';
import ProgressTrackingCard from './PlanComponents/ProgressTrackingCard';
import PlanVsActualCard from './PlanComponents/PlanVsActualCard';
import ActualResultsInput from './PlanComponents/ActualResultsInput';
import LoadingScreen from '../../components/LoadingScreen';

import AssistMenu from './AssistMenu';
import styled from '@mui/material/styles/styled';
import { activityTracker } from '../../utils/activityTracker';

// ======================== セグメントコントロール型Tabs ========================
const CustomTabs = styled(Tabs)(({ theme }) => ({
  ...segmentedTabsContainer('recruit_plan'),
}));

const CustomTab = styled((props) => <Tab disableRipple {...props} />)(
  ({ theme }) => ({
    ...segmentedTab('recruit_plan'),
  })
);

function NiceTabsExample({ tabValue, setTabValue, showActuals }) {
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const tabs = [
    { label: '採用要件' },
    { label: '採用計画' },
  ];
  if (showActuals) {
    tabs.push({ label: '実績管理' });
  }

  return (
    <CustomTabs
      value={tabValue}
      onChange={handleTabChange}
      variant="fullWidth"
      textColor="inherit"
      indicatorColor="secondary"
    >
      {tabs.map((tab, i) => (
        <CustomTab key={i} label={tab.label} />
      ))}
    </CustomTabs>
  );
}

// ======================== 定数 ========================
const API_BASE_URL = process.env.REACT_APP_API_URL;
const USE_MOCK = false;


// ======================== メインコンポーネント ========================
export default function RecruitPlanAssistant() {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  // 認証とトークン管理
  const { user: currentUser, loading: authLoading } = useAuth();

  // UIモード: "selection" | "form" | "loading" | "main"
  const [uiMode, setUiMode] = useState('selection');
  // チャットモード: "sidebar" | "popup" | "off"
  const [chatMode, setChatMode] = useState('off');

  // フォームの採用要件
  const [requirements, setRequirements] = useState({
    startDate: '',
    endDate: '',
    targetHires: '',
    budget: '',
    position: '',
    desiredSkillLevel: '',
    orgIssues: '',
    industry: '',
    recruitType: '',
    existingTalentSkill: '',
    pastRecruitData: '',
    employmentType: '',
    workStyle: '',
    prioritySchedule: '',
  });

  // AI結果データ(採用計画)
  const [planData, setPlanData] = useState(null);
  // 「決定予定」のプランID
  const [decidingPlanId, setDecidingPlanId] = useState(null);
  // チャットセッションID
  const [chatSessionId, setChatSessionId] = useState(null);

  // チャット
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const chatEndRef = useRef(null);

  // 雑談や要件反映のローディング
  const [brushupLoading, setBrushupLoading] = useState(false);

  // タブ
  const [tabValue, setTabValue] = useState(0);

  // 実績トラッキング（Phase 2）
  const [progressData, setProgressData] = useState(null);
  const [isSavingActuals, setIsSavingActuals] = useState(false);

  // 履歴ダイアログ
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyList, setHistoryList] = useState([]);
  const [historySearchQuery, setHistorySearchQuery] = useState('');
  // アシストメニュー
  const [anchorEl, setAnchorEl] = useState(null);

  // 採用計画決定ダイアログ
  const [decideDialogOpen, setDecideDialogOpen] = useState(false);

  // 削除確認ダイアログ
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // ====================== Snackbar (通知) ======================
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // 履歴ナビゲーション用の状態
  const [prevVersionId, setPrevVersionId] = useState(null);
  const [nextVersionId, setNextVersionId] = useState(null);
  const [hasPrevVersion, setHasPrevVersion] = useState(false);
  const [hasNextVersion, setHasNextVersion] = useState(false);

  // Navigation state
  const [navigationInfo, setNavigationInfo] = useState({
    prevId: null,
    nextId: null,
    prevInfo: null,
    nextInfo: null
  });

  // 自動保存用の状態
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saved', 'saving', 'unsaved', 'error'
  const autoSaveTimerRef = useRef(null);

  /** 通知を表示するためのヘルパー */
  const showSnackbar = (message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  // 自動保存関数
  const autoSaveRequirements = useCallback(async (requirementsToSave) => {
    if (!requirementsToSave || uiMode !== 'main') return;

    try {
      setSaveStatus('saving');

      const response = await axios.post(
        `${API_BASE_URL}/recruit_plan_assistant/update_requirements`,
        { requirements: requirementsToSave },
        { withCredentials: true }
      );

      if (response.data.status === 'success') {
        setSaveStatus('saved');
        // 保存済み表示は2秒後に自動的に消す
        setTimeout(() => {
          setSaveStatus('');
        }, 2000);
      } else {
        setSaveStatus('error');
      }
    } catch (error) {
      console.error('自動保存に失敗しました:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('unsaved'), 5000);
    }
  }, [uiMode]);

  // 採用要件の変更を監視して自動保存
  useEffect(() => {
    if (uiMode !== 'main' || !requirements) return;

    // 初回表示時は保存しない（空の要件の場合）
    if (!requirements.position || !requirements.startDate) return;

    setSaveStatus('unsaved');

    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveRequirements(requirements);
    }, 3000);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [requirements, uiMode, autoSaveRequirements]);

  // Add activity tracking
  useEffect(() => {
    const cleanup = activityTracker.trackPageView('recruit_plan');
    return () => cleanup();
  }, []);

  // -------------------- useEffect --------------------
  
  // 初期ロード時にローカルストレージからデータを復元
  useEffect(() => {
    const loadLastPlan = async () => {
      // 前回のコンテキストや履歴をローカルストレージから復元
      const savedData = localStorage.getItem("lastPlanFullData");
      if (savedData) {
        try {
          const parsedData = JSON.parse(savedData);
          // planData, chatSessionId, planId, requirementsが存在する場合に復元
          if (parsedData.planData && parsedData.chatSessionId && parsedData.planId) {
            setPlanData(parsedData.planData);
            setChatSessionId(parsedData.chatSessionId);
            setDecidingPlanId(parsedData.planId);
            
            if (parsedData.requirements) {
              setRequirements(parsedData.requirements);
            }
            
            // ナビゲーション情報も取得
            await fetchNavigationInfo(parsedData.planId);
            
            return true; // 復元成功
          }
        } catch (e) {
          console.error("ローカルストレージからの復元エラー:", e);
        }
      }
      return false; // 復元なし/失敗
    };

    const loadDataFlow = async () => {
      const restored = await loadLastPlan();
      if (!restored) {
        // 復元できなかった場合は最新の決定済みプランを試みる
        await handleLoadDecidedPlan();
      }

      // いずれにしてもガイドメッセージを設定
      setMessages(prevMessages => {
        if (prevMessages.length === 0) {
          return [
            { sender: "bot", text: "採用計画について、ご質問にお答えします!" }
          ];
        }
        return prevMessages;
      });
    };

    loadDataFlow();
  }, []);

  // ナビゲーションなどのUIイベントのハンドラー
  const handleLoadPlan = async (planId) => {
    if (!planId) return;
    
    setBrushupLoading(true);
    try {
      // 選択された計画を取得
      const resp = await axios.get(
        `${API_BASE_URL}/recruit_plan_assistant/plan/${planId}`,
        { withCredentials: true }
      );
      
      if (resp.data) {
        // planDataを更新
        setPlanData(resp.data.planData);
        setChatSessionId(resp.data.chatSessionId);
        setDecidingPlanId(resp.data.planId);
        
        // requirementsがあれば更新
        if (resp.data.requirements) {
          setRequirements(resp.data.requirements);
        }
        
        // ナビゲーション情報を取得
        await fetchNavigationInfo(planId);
        
        // ローカルストレージも更新
        localStorage.setItem("lastPlanFullData", JSON.stringify({
          planData: resp.data.planData,
          requirements: resp.data.requirements,
          planId: resp.data.planId,
          chatSessionId: resp.data.chatSessionId
        }));
        
        // メッセージも更新またはクリア
        if (resp.data.messages && resp.data.messages.length) {
          // 新しいメッセージのみ追加（履歴の重複を避ける）
          const lastMessage = resp.data.messages[resp.data.messages.length - 1];
          if (lastMessage && lastMessage.role === 'assistant') {
            setMessages(prev => {
              // 既存のメッセージとの重複チェック
              const isDuplicate = prev.some(msg => 
                msg.sender === 'bot' && msg.text === lastMessage.content
              );
              if (!isDuplicate) {
                return [...prev, { sender: 'bot', text: lastMessage.content }];
              }
              return prev;
            });
          }
        } else {
          // メッセージデータがなければリセット
          setMessages([
            { sender: "bot", text: "採用計画を作成しました。内容を確認し、必要であれば修正や質問をしてください。" }
          ]);
        }
        
        showSnackbar("採用計画を読み込みました");
      }
    } catch (err) {
      console.error("計画読み込みエラー:", err);
      showSnackbar("採用計画の読み込みに失敗しました", "error");
    } finally {
      setBrushupLoading(false);
    }
  };

  // -------------------------
  // サーバから直近の採用計画を取得
  // -------------------------
  const fetchLastPlanIfAny = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/recruit_plan_assistant/last_plan`, {
        withCredentials: true
      });
      if (res.data && res.data.planData) {
        setPlanData(res.data.planData);
        setChatSessionId(res.data.chatSessionId);
        setDecidingPlanId(res.data.planId || null);

        if (res.data.requirements) {
          setRequirements(res.data.requirements);
        }
        setUiMode('main');
      } else {
        // なければフォーム
        setUiMode('form');
      }
    } catch (err) {
      console.warn('fetchLastPlanIfAny error:', err);
      setUiMode('form');
    }
  };

  // -------------------------
  // サーバから直近の決定済み採用計画を取得
  // -------------------------
  const handleLoadDecidedPlan = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/recruit_plan_assistant/last_decided_plan`, {
        withCredentials: true
      });
      
      if (res.data && res.data.planData) {
        setPlanData(res.data.planData);
        setChatSessionId(res.data.chatSessionId);
        setDecidingPlanId(res.data.planId || null);

        if (res.data.requirements) {
          setRequirements(res.data.requirements);
        }
        
        // ナビゲーション情報も取得
        if (res.data.planId) {
          await fetchNavigationInfo(res.data.planId);
        }
        
        setUiMode('main');
        setMessages([
          { sender: "bot", text: "前回決定した採用計画を読み込みました。必要であれば修正や質問をしてください。" }
        ]);
        return true;
      }
      return false;
    } catch (err) {
      console.warn('handleLoadDecidedPlan error:', err);
      return false;
    }
  };

  // ---------------------- useEffect(1): 初回マウント時 ----------------------
  useEffect(() => {
    const storedData = localStorage.getItem("lastPlanFullData");
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      if (parsedData.planData) setPlanData(parsedData.planData);
      if (parsedData.requirements) setRequirements(parsedData.requirements);
      if (parsedData.planId) setDecidingPlanId(parsedData.planId);
      if (parsedData.chatSessionId) setChatSessionId(parsedData.chatSessionId);
      setUiMode('main');
    } else {
      fetchLastPlanIfAny();
    }
  }, []);

  // ---------------------- useEffect(2): planData/requirements更新時保存 ----------------------
  useEffect(() => {
    if (planData && requirements) {
      localStorage.setItem("lastPlanFullData", JSON.stringify({
        planData,
        requirements,
        planId: decidingPlanId,
        chatSessionId
      }));
    }
  }, [planData, requirements, decidingPlanId, chatSessionId]);

  // ---------------------- useEffect(2.5): 実績管理タブ切り替え時に進捗データ取得 ----------------------
  useEffect(() => {
    if (tabValue === 2 && planData && planData.is_decided) {
      const pid = planData.plan_id || planData.id || decidingPlanId;
      if (!pid) return;
      axios.get(
        `${API_BASE_URL}/recruit_plan_assistant/plans/${pid}/progress`,
        { withCredentials: true }
      ).then(res => {
        setProgressData(res.data);
      }).catch(err => {
        console.warn('[ProgressData] fetch failed:', err);
      });
    }
  }, [tabValue, planData, decidingPlanId]);

  // ---------------------- useEffect(3): チャットメッセージの更新毎に末尾へスクロール ----------------------
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // ----------------------
  // アシストメニュー
  // ----------------------
  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  // 「初めからやり直す」
  const handleClickRestart = () => {
    setPlanData(null);
    setUiMode('selection');
    setTabValue(0);
    setChatSessionId(null);
    setDecidingPlanId(null);
    setMessages([]);
    localStorage.removeItem("lastPlanFullData");
    handleMenuClose();

    showSnackbar("初めからやり直しました。");
  };

  // 「過去の出力結果を確認」
  const handleClickHistory = async () => {
    setHistoryOpen(true);
    handleMenuClose();
    try {
      const resp = await axios.get(`${API_BASE_URL}/recruit_plan_assistant/history`, {
        withCredentials: true
      });
      setHistoryList(resp.data);
    } catch (err) {
      alert('過去履歴の取得に失敗しました。');
    }
  };
  const handleCloseHistory = () => setHistoryOpen(false);

  // 削除ボタンクリック時
  const handleDeleteClick = (item, event) => {
    event.stopPropagation(); // 親要素のクリックイベントを防止
    setItemToDelete(item);
    setDeleteConfirmOpen(true);
  };

  // 削除実行
  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    setIsDeleting(true);

    try {
      await axios.patch(
        `${API_BASE_URL}/recruit_plan_assistant/plan/${itemToDelete.planId}`,
        {},
        { withCredentials: true }
      );

      // 成功：履歴リストから削除
      setHistoryList(prev => prev.filter(item => item.planId !== itemToDelete.planId));

      // 削除したデータが現在表示中だった場合、ローカルストレージもクリア
      if (itemToDelete.planId === decidingPlanId) {
        localStorage.removeItem('lastPlanFullData');
      }

      // 成功メッセージ
      showSnackbar('履歴を削除しました');

    } catch (error) {
      console.error('削除エラー:', error);
      showSnackbar('削除に失敗しました');
    } finally {
      setIsDeleting(false);
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
    }
  };

  // 履歴リストから選択
  const handleSelectHistoryItem = async (item) => {
    // planId の確認
    const planId = item.planId;
    if (!planId) {
      alert('この履歴には採用計画がありません。');
      return;
    }
    
    try {
      setBrushupLoading(true);
      
      // 再生成された計画の場合は plan/{plan_id} エンドポイントから詳細を取得
      if (item.type === "regenerated_plan") {
        const resp = await axios.get(
          `${API_BASE_URL}/recruit_plan_assistant/plan/${planId}`,
          { withCredentials: true }
        );
        
        // Update state with fetched data
        setPlanData(resp.data.planData);
        setDecidingPlanId(planId);
        setChatSessionId(resp.data.chatSessionId);
        
        if (resp.data.requirements) {
          setRequirements(resp.data.requirements);
        }
        
        // ナビゲーション情報を更新
        await fetchNavigationInfo(planId);
        
        setHistoryOpen(false);
        setUiMode('main');
        setTabValue(1);
        
        showSnackbar("再生成された採用計画を読み込みました。");
        return;
      }
      
      // 以下は従来の処理（セッション由来の計画）
      // 要件
      if (item.requirements) {
        setRequirements(item.requirements);
      } else {
        setRequirements({});
      }

      // planData
      let loadedPlanData = null;
      if (item.plan_data) {
        // 新形式: plan_data オブジェクトがある場合
        loadedPlanData = item.plan_data;
        setPlanData(loadedPlanData);
        setDecidingPlanId(planId);
      } else if (item.latest_plan_md) {
        // 旧形式: latest_plan_md から解析
        try {
          const parsed = JSON.parse(item.latest_plan_md);
          loadedPlanData = {
            id: planId || null,
            difficulty: parsed.difficulty || '',

            overview: parsed.overview || '',
            recommendedHiresAndApplicants: parsed.recommendedHiresAndApplicants || {},
            recruitsPeriod: parsed.recruitsPeriod || '',
            budgetDistribution: parsed.budgetDistribution || {},
            recommendedSkills: parsed.recommendedSkills || [],
            timeline: parsed.timeline || [],
            advices: parsed.advices || {},
            extInfo: item.requirements || {},
          };
          setPlanData(loadedPlanData);
          setDecidingPlanId(planId);
        } catch (e) {
          alert('この履歴の計画データはJSONではありません。');
          setPlanData(null);
          setDecidingPlanId(null);
        }
      } else {
        setPlanData(null);
        setDecidingPlanId(null);
      }

      // chatSessionId
      setChatSessionId(item.id);
      
      // ナビゲーション情報も更新
      if (planId) {
        await fetchNavigationInfo(planId);
      }

      // ローカル保存
      localStorage.setItem("lastPlanFullData", JSON.stringify({
        planData: loadedPlanData,
        requirements: item.requirements || {},
        planId: planId || null,
        chatSessionId: item.id
      }));

      setHistoryOpen(false);
      setUiMode('main');
      setTabValue(1);

      // 変更がわかるように通知
      showSnackbar("過去の採用計画を読み込みました。");
    } catch (err) {
      console.error("計画読み込みエラー:", err);
      showSnackbar("採用計画の読み込みに失敗しました", "error");
    } finally {
      setBrushupLoading(false);
    }
  };

  // ----------------------
  // 採用計画の最終決定
  // ----------------------
  const handleDecidePlan = async () => {
    if (!decidingPlanId) {
      alert("決定するプランIDが取得できません");
      return;
    }
    try {
      await axios.post(`${API_BASE_URL}/recruit_plan_assistant/decide_plan/${decidingPlanId}`, {}, {
        withCredentials: true
      });
      setDecideDialogOpen(true);

      // planData に決定フラグを設定（常に更新）
      if (planData) {
        setPlanData({ ...planData, is_decided: true });
      }

      // 履歴リストにも反映
      setHistoryList(prev => prev.map(h => {
        if (h.planId === decidingPlanId) return { ...h, is_decided: true };
        return { ...h, is_decided: false };
      }));

      showSnackbar("採用計画を決定しました");

    } catch (err) {
      showError(err, '採用計画の決定に失敗しました');
    }
  };

  const handleCloseDecideDialog = () => {
    setDecideDialogOpen(false);
  };

  // ----------------------
  // フォーム送信 => 初回生成
  // ----------------------
  const handleFormSubmit = async (formData) => {
    setUiMode('loading');
    setRequirements(formData);

    if (USE_MOCK) {
      const mockId = 9999;
      const mockPlan = {
        id: mockId,
        difficulty: 'やや困難',
        overview: `エンジニア不足。人数:${formData.targetHires}`,
        recommendedHiresAndApplicants: {
          recommendedHires: formData.targetHires,
          requiredApplicants: '30'
        },
        recruitsPeriod: '3〜4か月',
        budgetDistribution: { media: 30, agency: 50, others: 20 },
        recommendedSkills: [],
        timeline: [],
        advices: {},
        extInfo: { ...formData }
      };
      setPlanData(mockPlan);
      setChatSessionId(mockId);
      setDecidingPlanId(mockId);
      setMessages([{ sender: 'bot', text: 'モックの採用計画を生成しました。' }]);
      setUiMode('main');
      showSnackbar("モックの採用計画を生成しました。(デモ)");
      return;
    }

    try {
      const resp = await axios.post(`${API_BASE_URL}/recruit_plan_assistant/generate_plan`, 
        formData
      , { withCredentials: true });

      setChatSessionId(resp.data.chatSessionId);
      setPlanData(resp.data.planData);
      setDecidingPlanId(resp.data.planId);
      if (resp.data.requirements) {
        setRequirements(resp.data.requirements);
      }
      setMessages([{ sender: 'bot', text: '採用計画を受け取りました！' }]);
      setUiMode('main');

      showSnackbar("新しい採用計画を生成しました。");
    } catch (err) {
      alert('採用計画の生成に失敗しました。');
      setUiMode('form');
    }
  };

  // (A) 雑談チャット送信
  const handleChatSend = async () => {
    if (!chatSessionId) {
      console.error("chatSessionIDがありません");
      alert("Chat session ID がありません");
      return;
    }
    if (!userInput.trim()) {
      console.error("ユーザー入力が空です");
      alert("メッセージを入力してください");
      return;
    }
    setIsTyping(true);
    setBrushupLoading(true);

    try {
      const resp = await axios.post(`${API_BASE_URL}/recruit_plan_assistant/chat`, {
        chatSessionId,
        userMessage: userInput
      }, { withCredentials: true });

      // 新しいメッセージのみ追加（履歴の重複を避ける）
      const lastMessage = resp.data.messages[resp.data.messages.length - 1];
      if (lastMessage && lastMessage.role === 'assistant') {
        setMessages(prev => {
          // 既存のメッセージとの重複チェック
          const isDuplicate = prev.some(msg => 
            msg.sender === 'bot' && msg.text === lastMessage.content
          );
          if (!isDuplicate) {
            return [...prev, { sender: 'bot', text: lastMessage.content }];
          }
          return prev;
        });
      }
      setUserInput("");
    } catch (err) {
      console.error("雑談API呼び出しエラー:", err);
      if (err.response) {
        console.error("エラーレスポンスデータ:", err.response.data);
        alert(`チャット送信エラー: ${JSON.stringify(err.response.data || {})}`);
      } else if (err.request) {
        console.error("リクエストエラー - レスポンスなし");
        alert("サーバーからの応答がありません。ネットワーク接続を確認してください。");
      } else {
        console.error("リクエスト設定エラー:", err.message);
        alert(`チャット送信エラー: ${err.message}`);
      }
    } finally {
      setIsTyping(false);
      setBrushupLoading(false);
    }
  };

  // (B) 要件反映 (再生成)
  const handleReflectPlan = async () => {
    if (!decidingPlanId) {
      alert("Plan IDがありません");
      return;
    }
    
    // Ensure planId is an integer
    const planIdInt = parseInt(decidingPlanId, 10);
    if (isNaN(planIdInt)) {
      alert(`計画IDが不正な値です: ${decidingPlanId}`);
      return;
    }
    
    if (!userInput.trim()) {
      alert("変更要望を入力してください");
      return;
    }
    setIsTyping(true);
    setBrushupLoading(true);

    try {
      const resp = await axios.post(`${API_BASE_URL}/recruit_plan_assistant/regenerate_plan`, {
        planId: planIdInt,
        userMessage: userInput
      }, { withCredentials: true });

      // サーバから返ってきた新しい planData と requirements を更新
      setPlanData(resp.data.planData);
      
      // 新しい計画IDを設定
      setDecidingPlanId(resp.data.planId);
      
      // 親IDを設定して前のバージョンへ戻れるようにする
      if (resp.data.parentId) {
        setPrevVersionId(resp.data.parentId);
        setHasPrevVersion(true);
      }
      
      // ナビゲーション情報を更新
      fetchNavigationInfo(resp.data.planId);

      // 重要: 更新された要件を状態に反映
      if (resp.data.requirements) {
        setRequirements(resp.data.requirements);
      }

      // サーバ側で返された messages も更新（もしあれば）
      if (resp.data.messages) {
        // 新しいメッセージのみ追加（履歴の重複を避ける）
        const lastMessage = resp.data.messages[resp.data.messages.length - 1];
        if (lastMessage && lastMessage.role === 'assistant') {
          setMessages(prev => {
            // 既存のメッセージとの重複チェック
            const isDuplicate = prev.some(msg => 
              msg.sender === 'bot' && msg.text === lastMessage.content
            );
            if (!isDuplicate) {
              return [...prev, { sender: 'bot', text: lastMessage.content }];
            }
            return prev;
          });
        }
      }

      // --- ここで diffSummary をチェック ---
      if (resp.data.diffSummary) {
        // 新たな bot メッセージとして差分サマリを追加
        setMessages(prevMessages => [
          ...prevMessages,
          { sender: 'bot', text: `変更点:\n${resp.data.diffSummary}` }
        ]);
      }

      // ローカルストレージも更新
      localStorage.setItem("lastPlanFullData", JSON.stringify({
        planData: resp.data.planData,
        requirements: resp.data.requirements || requirements,
        planId: resp.data.planId,
        chatSessionId: resp.data.chatSessionId
      }));

      // userInputクリア
      setUserInput("");

      // Snackbar等の通知
      showSnackbar("採用計画を再生成しました。");
    } catch (err) {
      console.error("要件反映失敗:", err);
      showError(err, '要件反映に失敗しました');
    } finally {
      setIsTyping(false);
      setBrushupLoading(false);
    }
  };

  // -- 採用計画の再生成ハンドラーを追加 --
  // 特定のフィールド値を変更して採用計画を再生成する
  const handleRegeneratePlan = async (field, value) => {
    const endTracking = await activityTracker.trackFeatureUse('recruit_plan_regenerate', {
      field,
      value
    });
    
    setBrushupLoading(true);
    try {
      // Ensure planId is an integer
      const planIdInt = parseInt(decidingPlanId, 10);
      if (!planIdInt || isNaN(planIdInt)) {
        throw new Error(`計画IDが不正な値です: ${decidingPlanId}`);
      }
      
      const updateMessage = `${field}を「${value}」に更新して採用計画を再生成します。`;
      
      const resp = await axios.post(
        `${API_BASE_URL}/recruit_plan_assistant/regenerate_plan`,
        {
          planId: planIdInt,
          userMessage: updateMessage,
          updateField: field,
          updateValue: value
        },
        { withCredentials: true }
      );
      
      if (resp.data) {
        // 新しい採用計画をセット
        setPlanData(resp.data.planData);
        setDecidingPlanId(resp.data.planId);
        
        // チャットメッセージを更新
        if (resp.data.messages && resp.data.messages.length) {
          // 新しいメッセージのみ追加（履歴の重複を避ける）
          const lastMessage = resp.data.messages[resp.data.messages.length - 1];
          if (lastMessage && lastMessage.role === 'assistant') {
            setMessages(prev => {
              // 既存のメッセージとの重複チェック
              const isDuplicate = prev.some(msg => 
                msg.sender === 'bot' && msg.text === lastMessage.content
              );
              if (!isDuplicate) {
                return [...prev, { sender: 'bot', text: lastMessage.content }];
              }
              return prev;
            });
          }
        } else {
          // メッセージが返ってこなかった場合のフォールバック
          setMessages([
            ...messages,
            { sender: 'user', text: updateMessage },
            { sender: 'bot', text: `${field}を「${value}」に更新した採用計画を再生成しました。` }
          ]);
        }
        
        // 変更点の通知
        if (resp.data.diffSummary) {
          showSnackbar(resp.data.diffSummary);
        } else {
          showSnackbar(`${field}を更新して採用計画を再生成しました。`);
        }
      }
    } catch (err) {
      console.error("採用計画の再生成に失敗しました:", err);
      if (err.response) {
        console.error("エラーレスポンス:", err.response.data);
        showSnackbar(`採用計画の再生成に失敗しました: ${JSON.stringify(err.response.data)}`, "error");
      } else {
        showSnackbar("採用計画の再生成に失敗しました。", "error");
      }
    } finally {
      setBrushupLoading(false);
      endTracking();
    }
  };

  // -- 採用要件を更新して採用計画を再生成するハンドラー --
  const handleUpdateRequirements = async (updatedRequirements) => {
    const endTracking = await activityTracker.trackFeatureUse('recruit_plan_update', {
      requirements: updatedRequirements
    });
    
    setBrushupLoading(true);
    try {
      // Ensure planId is an integer
      const planIdInt = parseInt(decidingPlanId, 10);
      if (!planIdInt || isNaN(planIdInt)) {
        throw new Error(`計画IDが不正な値です: ${decidingPlanId}`);
      }
      
      // 要件の変更点を特定してメッセージを作成
      const changedFields = [];
      for (const key in updatedRequirements) {
        if (updatedRequirements[key] !== requirements[key]) {
          changedFields.push(`${key}:「${updatedRequirements[key]}」`);
        }
      }
      

      // 要件変更メッセージを作成
      let message = changedFields.length > 0
        ? `以下の項目を更新して採用計画を再作成してください:\n${changedFields.join('\n')}`
        : '採用計画を再作成してください';

      // 重要: APIリクエスト前に要件を更新して、UI表示を確実に更新
      setRequirements(updatedRequirements);
      
      // APIリクエスト
      const response = await axios.post(
        `${API_BASE_URL}/recruit_plan_assistant/regenerate_plan`,
        {
          planId: planIdInt,
          userMessage: message,
          updatedRequirements: updatedRequirements  // 更新された要件全体を送信
        },
        { withCredentials: true }
      );
      
      // 成功時の処理
      if (response.data) {
        // 新しい計画をセット
        setDecidingPlanId(response.data.planId);
        setPlanData(response.data.planData);
        
        // 重要: サーバーからの要件よりもユーザーの入力を優先
        if (response.data.requirements) {
          // マージして確実にユーザーの編集内容を保持
          const mergedRequirements = {
            ...response.data.requirements,
            ...updatedRequirements  // ユーザーの編集内容を優先
          };
          setRequirements(mergedRequirements);
        } else {
          // サーバーから要件が返ってこない場合はユーザーの入力をそのまま使用
          setRequirements(updatedRequirements);
        }
        
        // ナビゲーション情報を更新
        fetchNavigationInfo(response.data.planId);
      }
      
      return response;
    } catch (error) {
      console.error("採用計画再生成エラー:", error);
      throw error;
    } finally {
      setBrushupLoading(false);
      endTracking();
    }
  };

  // チャットモードのラベル
  const getChatModeLabel = (mode) => {
    switch (mode) {
      case 'sidebar': return 'サイドバー';
      case 'popup': return 'ポップアップ';
      default: return 'オフ';
    }
  };

  // Fetch navigation info for a given plan
  const fetchNavigationInfo = async (planId) => {
    if (!planId) return;
    
    try {
      const response = await axios.get(
        `${API_BASE_URL}/recruit_plan_assistant/navigation/${planId}`,
        { withCredentials: true }
      );
      
      setNavigationInfo(response.data);
      
      // Update hasPrevVersion based on navigation info
      setHasPrevVersion(!!response.data.prevId);
      setPrevVersionId(response.data.prevId);
      
      // Update hasNextVersion based on navigation info
      setHasNextVersion(!!response.data.nextId);
    } catch (err) {
      console.error("Failed to fetch navigation info:", err);
    }
  };

  // Navigate to a specific version of plan
  const navigateToVersion = async (planId) => {
    if (!planId) return;
    
    setBrushupLoading(true);
    try {
      // Fetch plan data for the specified ID
      const resp = await axios.get(
        `${API_BASE_URL}/recruit_plan_assistant/plan/${planId}`,
        { withCredentials: true }
      );
      
      // Update state with fetched data
      setPlanData(resp.data.planData);
      setDecidingPlanId(planId);
      
      if (resp.data.requirements) {
        setRequirements(resp.data.requirements);
      }
      
      // Update navigation info for the new plan
      await fetchNavigationInfo(planId);
      
      showSnackbar("別バージョンの採用計画を表示しています");
    } catch (err) {
      console.error("Failed to navigate to version:", err);
      showSnackbar("採用計画の取得に失敗しました", "error");
    } finally {
      setBrushupLoading(false);
    }
  };

  // Navigate to previous version
  const handlePrevVersion = () => {
    if (navigationInfo.prevId) {
      navigateToVersion(navigationInfo.prevId);
    }
  };

  // Navigate to next version
  const handleNextVersion = () => {
    if (navigationInfo.nextId) {
      navigateToVersion(navigationInfo.nextId);
    }
  };

  // =============== UI描画 ===============
  return (
    <AppLayout>
      <Box
        sx={{
          marginRight: !isSmDown && chatMode === 'sidebar' ? '400px' : 0,
          transition: theme.transitions.create(['margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >

          {/* AIアシスト機能ボタン */}
          {uiMode === 'main' && (
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
              <Tooltip
                title={
                  chatMode === 'off' ? 'AIに相談' :
                  chatMode === 'sidebar' ? 'サイドバーチャット（クリックでポップアップに切替）' :
                  'ポップアップチャット（クリックでオフに切替）'
                }
                arrow
              >
                <IconButton
                  onClick={() => {
                    // チャットモード切り替え
                    if (chatMode === 'sidebar') setChatMode('popup');
                    else if (chatMode === 'popup') setChatMode('off');
                    else setChatMode('sidebar');
                  }}
                  sx={chatIconButton('recruit_plan', chatMode !== 'off')}
                >
                  <ForumIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="その他の操作" arrow>
                <IconButton
                  onClick={handleMenuOpen}
                  sx={menuIconButton('recruit_plan')}
                >
                  <MoreVertIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}

          {/* UIモード: selection */}
          {uiMode === 'selection' && (
            <Container maxWidth="lg" sx={{ py: 6 }}>
              <Box
                component={motion.div}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
                sx={{ textAlign: 'center', mb: 6 }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    mb: 2,
                    fontWeight: 700,
                    color: recruitPlanTheme.primary,
                  }}
                >
                  採用計画アシストの利用方法を選択
                </Typography>
                <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                  以下の2つの方法から採用計画の作成方法を選んでください
                </Typography>
              </Box>

              <Grid container spacing={4} justifyContent="center" sx={{ mb: 4 }}>
                {/* 新規作成カード */}
                <Grid item xs={12} md={6}>
                  <Paper
                    component={motion.div}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    whileHover={{ y: -8 }}
                    onClick={() => setUiMode('form')}
                    sx={{
                      ...selectionCard('recruit_plan', theme),
                      minHeight: 400,
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                    }}
                  >
                    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 4 }}>
                      {/* Icon */}
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                        <Box
                          sx={{
                            width: 64,
                            height: 64,
                            borderRadius: '12px',
                            backgroundColor: `${recruitPlanTheme.primary}15`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <RestartAltIcon sx={{ fontSize: 32, color: recruitPlanTheme.primary }} />
                        </Box>
                      </Box>

                      {/* Content */}
                      <Box sx={{ flexGrow: 1, textAlign: 'center', mb: 3 }}>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 600,
                            color: textColors(theme).primary,
                            mb: 2,
                          }}
                        >
                          新規作成
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: 'text.secondary',
                            lineHeight: 1.6,
                            mb: 3,
                          }}
                        >
                          採用要件から新しい採用計画を作成します。必要な情報を入力して、AIが最適な採用計画を生成します。
                        </Typography>

                        <Box sx={{
                          p: 2,
                          bgcolor: `${recruitPlanTheme.primary}08`,
                          borderRadius: 2,
                          textAlign: 'left'
                        }}>
                          <Typography variant="subtitle2" sx={{ mb: 1.5, color: recruitPlanTheme.primary, fontWeight: 600 }}>
                            主な入力項目:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {['募集職種', '採用人数', '予算', '期間', 'スキル要件', '組織課題'].map((item) => (
                              <Box key={item} sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: recruitPlanTheme.primary, mr: 0.75 }}/>
                                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{item}</Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      </Box>

                      {/* Action Button */}
                      <Button
                        variant="contained"
                        sx={{
                          ...selectionCardButton('recruit_plan'),
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setUiMode('form');
                        }}
                      >
                        この方法で作成する
                      </Button>
                    </Box>
                  </Paper>
                </Grid>

                {/* 過去の計画から選択カード */}
                <Grid item xs={12} md={6}>
                  <Paper
                    component={motion.div}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    whileHover={{ y: -8 }}
                    onClick={handleClickHistory}
                    sx={{
                      ...selectionCard('recruit_plan', theme),
                      minHeight: 400,
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                    }}
                  >
                    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 4 }}>
                      {/* Icon */}
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                        <Box
                          sx={{
                            width: 64,
                            height: 64,
                            borderRadius: '12px',
                            backgroundColor: `${recruitPlanTheme.primary}15`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <HistoryIcon sx={{ fontSize: 32, color: recruitPlanTheme.primary }} />
                        </Box>
                      </Box>

                      {/* Content */}
                      <Box sx={{ flexGrow: 1, textAlign: 'center', mb: 3 }}>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 600,
                            color: textColors(theme).primary,
                            mb: 2,
                          }}
                        >
                          過去の計画から選択
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: 'text.secondary',
                            lineHeight: 1.6,
                            mb: 3,
                          }}
                        >
                          過去に作成した採用計画を確認・再利用します。履歴から選択して、必要に応じて修正できます。
                        </Typography>

                        <Box sx={{
                          p: 2,
                          bgcolor: `${recruitPlanTheme.primary}08`,
                          borderRadius: 2,
                          textAlign: 'left'
                        }}>
                          <Typography variant="subtitle2" sx={{ mb: 1.5, color: recruitPlanTheme.primary, fontWeight: 600 }}>
                            できること:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {['過去の計画を確認', '決定済み計画を再利用', '計画を修正', '履歴を検索'].map((item) => (
                              <Box key={item} sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: recruitPlanTheme.primary, mr: 0.75 }}/>
                                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{item}</Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      </Box>

                      {/* Action Button */}
                      <Button
                        variant="contained"
                        sx={{
                          ...selectionCardButton('recruit_plan'),
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClickHistory();
                        }}
                      >
                        この方法で選択する
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Container>
          )}

          {/* UIモード: form */}
          {uiMode === 'form' && (
            <Box sx={{ maxWidth: 720, mx: 'auto' }}>
              <RecruitPlanForm
                defaultValues={requirements}
                onSubmit={handleFormSubmit}
                onBack={() => setUiMode('selection')}
              />
            </Box>
          )}
          {/* UIモード: loading */}
          {uiMode === 'loading' && <LoadingScreen
            message="採用計画を生成中..."
            loadingType="recruit_plan"
            featureKey="recruit_plan"
          />}

          {/* UIモード: main */}
          {uiMode === 'main' && (
            <Box
              component={motion.div}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              sx={{
                flexGrow: 1,
                overflow: 'auto',
                py: 3,
                px: { xs: 2, md: 3 },
                backgroundColor: bg(theme).card,
                maxWidth: 1200,
                mx: 'auto'
              }}
            >
              {/* ステータスバー */}
              <AnimatePresence>
                {planData && (
                  <Alert
                    component={motion.div}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    severity={planData.is_decided ? "success" : "info"}
                    icon={planData.is_decided ? <CheckCircleOutlineIcon /> : <InfoOutlinedIcon />}
                    sx={{
                      mb: 3,
                      borderRadius: 2,
                      '& .MuiAlert-message': {
                        width: '100%'
                      }
                    }}
                  >
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {planData.is_decided ? 'この採用計画は決定済みです' : 'この採用計画は未決定です'}
                      </Typography>
                      <Typography variant="body2">
                        {planData.is_decided
                          ? '求人票作成や媒体選定でこの計画データを活用でき、参照・共有が可能です'
                          : '決定すると求人票作成や媒体選定でこの計画データを活用でき、AI提案の精度も向上します'}
                      </Typography>
                    </Box>
                  </Alert>
                )}
              </AnimatePresence>

              <NiceTabsExample
                tabValue={tabValue}
                setTabValue={setTabValue}
                showActuals={planData && planData.is_decided}
              />

              {/* 自動保存ステータス表示 - 必要な時だけ表示 */}
              {tabValue === 0 && saveStatus && saveStatus !== '' && (
                <Box
                  sx={{
                    position: 'fixed',
                    top: 80,
                    right: 20,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    backgroundColor: adaptiveColor(theme, 'rgba(255,255,255,0.95)', 'rgba(30,30,30,0.95)'),
                    padding: '8px 16px',
                    borderRadius: '20px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    zIndex: 1000
                  }}
                >
                  {saveStatus === 'saving' && (
                    <>
                      <CircularProgress size={16} sx={{ color: 'primary.main' }} />
                      <Typography variant="caption">保存中...</Typography>
                    </>
                  )}
                  {saveStatus === 'saved' && (
                    <>
                      <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
                      <Typography variant="caption">保存済み</Typography>
                    </>
                  )}
                  {saveStatus === 'unsaved' && (
                    <>
                      <SaveIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                      <Typography variant="caption">未保存の変更</Typography>
                    </>
                  )}
                  {saveStatus === 'error' && (
                    <>
                      <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />
                      <Typography variant="caption">保存エラー</Typography>
                    </>
                  )}
                </Box>
              )}

              {tabValue === 0 && (
                <RequirementsView 
                  requirements={requirements} 
                  onRequirementsChange={handleUpdateRequirements}
                />
              )}
              {tabValue === 1 && (
                <PlanView
                  planData={planData}
                  budgetYen={requirements.budget}
                  requirements={requirements}
                  onRegeneratePlan={handleRegeneratePlan}
                />
              )}

              {/* 実績管理タブ（決定済みの場合のみ） */}
              {tabValue === 2 && planData && planData.is_decided && (
                <Box sx={{ mt: 2, mx: 'auto', width: '100%', maxWidth: { xs: 360, sm: 600, md: 1800 } }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6} component={motion.div}
                      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0 }}>
                      <ProgressTrackingCard progressData={progressData} />
                    </Grid>
                    <Grid item xs={12} md={6} component={motion.div}
                      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.1 }}>
                      <PlanVsActualCard progressData={progressData} />
                    </Grid>
                    <Grid item xs={12} component={motion.div}
                      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.2 }}>
                      <ActualResultsInput
                        planId={planData.plan_id || planData.id || decidingPlanId}
                        actuals={progressData?.actuals}
                        isSaving={isSavingActuals}
                        onSave={async (payload) => {
                          setIsSavingActuals(true);
                          try {
                            const pid = planData.plan_id || planData.id || decidingPlanId;
                            await axios.put(
                              `${API_BASE_URL}/recruit_plan_assistant/plans/${pid}/actual_results`,
                              payload,
                              { withCredentials: true }
                            );
                            // 保存後に進捗データを再取得
                            const res = await axios.get(
                              `${API_BASE_URL}/recruit_plan_assistant/plans/${pid}/progress`,
                              { withCredentials: true }
                            );
                            setProgressData(res.data);
                          } finally {
                            setIsSavingActuals(false);
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* FAB - 未決定の場合のみ表示 */}
              {planData && !planData.is_decided && (
                <Fab
                  component={motion.button}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 260, damping: 20 }}
                  variant="extended"
                  onClick={handleDecidePlan}
                  sx={{
                    position: 'fixed',
                    bottom: 24,
                    right: 24,
                    backgroundColor: '#f37335',
                    color: 'white',
                    px: 3,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 700,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    '&:hover': {
                      backgroundColor: '#e05a20',
                      boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <CheckIcon sx={{ mr: 1 }} />
                  この計画を決定
                </Fab>
              )}
            </Box>
          )}
      </Box>

      {/* チャット (ポップアップ / サイドバー) */}
      {uiMode === 'main' && chatMode === 'popup' && (
        <AIChat
          messages={messages}
          userInput={userInput}
          setUserInput={setUserInput}
          isTyping={isTyping}
          chatLoading={brushupLoading}
          reflectLoading={brushupLoading}
          isLoading={brushupLoading}
          chatEndRef={chatEndRef}
          onChatSend={handleChatSend}
          onReflectPlan={handleReflectPlan}
          initialGuideMessage="AIポップアップチャットです。"
          iconTooltipMessage="AIに相談＆要件変更できます。"
          chatButtonLabel="AIに相談"
          planButtonLabel="採用計画を修正"
          featureKey="recruit_plan"
        />
      )}
      {uiMode === 'main' && chatMode === 'sidebar' && (
        <AIChatSide
          messages={messages}
          userInput={userInput}
          setUserInput={setUserInput}
          isTyping={isTyping}
          chatLoading={brushupLoading}
          reflectLoading={brushupLoading}
          isLoading={brushupLoading}
          onChatSend={handleChatSend}
          onReflectPlan={handleReflectPlan}
          chatEndRef={chatEndRef}
          initialGuideMessage="サイドチャットでいつでもAIに相談できます。"
          onClose={() => setChatMode('popup')}
          chatButtonLabel="AIに相談"
          planButtonLabel="採用計画を修正"
          featureKey="recruit_plan"
        />
      )}

      {/* アシストメニュー */}
      <AssistMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onRestart={handleClickRestart}
        onShowHistory={handleClickHistory}
        onDecide={handleDecidePlan}
        onPrevVersion={handlePrevVersion}
        onNextVersion={handleNextVersion}
        hasPrevVersion={hasPrevVersion}
        hasNextVersion={hasNextVersion}
      />

      {/* 採用計画決定ダイアログ */}
      <Dialog open={decideDialogOpen} onClose={handleCloseDecideDialog}>
        <DialogTitle>採用計画を確定しました</DialogTitle>
        <DialogContent>
          <Typography>
            現在表示中の採用計画を確定しました。<br />
            (再度決定する場合は他のプランを決定すると上書きされます)
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDecideDialog}>OK</Button>
        </DialogActions>
      </Dialog>

      {/* 過去履歴ダイアログ - Timeline形式 */}
      <Dialog open={historyOpen} onClose={handleCloseHistory} fullWidth maxWidth="md">
        {/* ヘッダー */}
        <Box
          sx={{
            backgroundColor: '#f37335',
            color: 'white',
            px: 3,
            py: 3
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            過去の採用計画履歴
          </Typography>

          {/* 検索バー */}
          <TextField
            fullWidth
            placeholder="職種、キーワードで検索..."
            size="small"
            value={historySearchQuery}
            onChange={(e) => setHistorySearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'white' }} />
                </InputAdornment>
              ),
              sx: {
                backgroundColor: alpha('#ffffff', 0.2),
                borderRadius: 3,
                color: 'white',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                '&:hover': {
                  backgroundColor: alpha('#ffffff', 0.25)
                },
                '& input::placeholder': {
                  color: alpha('#ffffff', 0.8),
                  opacity: 1
                },
                '& input': {
                  color: 'white'
                }
              }
            }}
          />
        </Box>

        <DialogContent sx={{ p: 3, backgroundColor: bg(theme).subtle }}>
          {historyList.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <AccessTimeIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                履歴がありません
              </Typography>
            </Box>
          ) : (
            <Box sx={{ position: 'relative' }}>
              {/* Timeline縦線 */}
              <Box
                sx={{
                  position: 'absolute',
                  left: 24,
                  top: 0,
                  bottom: 0,
                  width: 2,
                  backgroundColor: '#f37335',
                  opacity: 0.25
                }}
              />

              {/* Timeline Items */}
              <AnimatePresence>
                {historyList
                  .filter((item) => {
                    if (!historySearchQuery.trim()) return true;
                    const query = historySearchQuery.toLowerCase();
                    const pos = (item.position || item.requirements?.position || '').toLowerCase();
                    const planData = item.plan_data || (item.latest_plan_md ? JSON.parse(item.latest_plan_md) : null);
                    const difficulty = planData?.difficulty?.toLowerCase() || '';
                    const overview = planData?.overview?.toLowerCase() || '';
                    const planId = String(item.planId || '');
                    const sessionId = String(item.id || '');

                    return pos.includes(query) ||
                           difficulty.includes(query) ||
                           overview.includes(query) ||
                           planId.includes(query) ||
                           sessionId.includes(query);
                  })
                  .map((item, index) => {
                  let planSummary = null;
                  let parsed = null;

                  if (!item.latest_plan_md && !item.plan_data) {
                    planSummary = (
                      <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                        Plan data not found
                      </Typography>
                    );
                  } else {
                    try {
                      parsed = item.plan_data || JSON.parse(item.latest_plan_md);
                      planSummary = (
                        <Box sx={{ mt: 2 }}>
                          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Box sx={{ flex: 1, minWidth: 200 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                難易度
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {typeof parsed.difficulty === 'object' && parsed.difficulty !== null ? parsed.difficulty.level : (parsed.difficulty || '未設定')}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                              概要
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.6 }}>
                              {(parsed.overview || '').slice(0, 100)}...
                            </Typography>
                          </Box>
                        </Box>
                      );
                    } catch (err) {
                      planSummary = (
                        <Typography variant="body2" sx={{ color: 'error.main' }}>
                          データ解析エラー
                        </Typography>
                      );
                    }
                  }

                  const isCurrent = decidingPlanId && item.planId && (decidingPlanId === item.planId);
                  const pos = item.position || item.requirements?.position || '職種不明';
                  const itemDate = new Date(item.timestamp || item.created_at);

                  return (
                    <Box
                      key={item.planId || item.id}
                      component={motion.div}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{
                        duration: 0.25,
                        delay: Math.min(index * 0.05, 0.3),
                        ease: 'easeOut'
                      }}
                      sx={{ position: 'relative', pl: 7, pb: 4, '&:last-child': { pb: 0 } }}
                    >
                      {/* Timeline Icon */}
                      <Box
                        sx={{
                          position: 'absolute',
                          left: 12,
                          top: 0,
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          backgroundColor: item.is_decided ? '#4caf50' : '#f37335',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                          zIndex: 1
                        }}
                      >
                        {item.is_decided ? (
                          <CheckIcon sx={{ fontSize: 14, color: 'white' }} />
                        ) : (
                          <AccessTimeIcon sx={{ fontSize: 14, color: 'white' }} />
                        )}
                      </Box>

                      {/* Timeline Card */}
                      <Box
                        component={motion.div}
                        whileHover={{ y: -4, transition: { duration: 0.2 } }}
                        sx={{
                          position: 'relative',
                          backgroundColor: bg(theme).card,
                          borderRadius: 2,
                          p: 2.5,
                          boxShadow: shadow(theme).sm,
                          border: '1px solid',
                          borderColor: item.is_decided ? 'success.light' : 'divider',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                            borderColor: item.is_decided ? 'success.main' : 'primary.main'
                          }
                        }}
                      >
                        {/* Card Header */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <WorkIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                                {pos}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              {itemDate.toLocaleDateString('ja-JP', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                timeZone: 'Asia/Tokyo'
                              })}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            {item.is_decided && (
                              <Chip
                                label="決定済み"
                                size="small"
                                icon={<CheckIcon />}
                                sx={{
                                  background: 'linear-gradient(135deg, #4caf50, #66bb6a)',
                                  color: 'white',
                                  fontWeight: 600,
                                  boxShadow: '0 2px 4px rgba(76, 175, 80, 0.3)'
                                }}
                              />
                            )}
                            {item.type === "regenerated_plan" && (
                              <Chip
                                label="再生成"
                                size="small"
                                sx={{
                                  bgcolor: alpha('#f37335', 0.1),
                                  color: '#f37335',
                                  fontWeight: 600
                                }}
                              />
                            )}
                            {isCurrent && (
                              <Chip
                                label="表示中"
                                size="small"
                                sx={{
                                  bgcolor: alpha('#ef5350', 0.1),
                                  color: '#ef5350',
                                  fontWeight: 600
                                }}
                              />
                            )}
                          </Box>
                        </Box>

                        {/* Card Content */}
                        {planSummary}

                        {/* Card Actions */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mt: 2 }}>
                          <Button
                            variant={isCurrent ? "outlined" : "contained"}
                            size="small"
                            disabled={isCurrent}
                            onClick={!isCurrent ? () => handleSelectHistoryItem(item) : undefined}
                            sx={{
                              borderRadius: 20,
                              px: 3,
                              fontWeight: 600,
                              textTransform: 'none',
                              ...(!isCurrent && {
                                background: 'linear-gradient(135deg, #f37335, #ff9966)',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #e2682f, #ff8855)',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 4px 12px rgba(243, 115, 53, 0.4)'
                                }
                              })
                            }}
                          >
                            {isCurrent ? "表示中" : "この計画を表示"}
                          </Button>

                          {/* 削除ボタン（現在表示中でない場合のみ表示） */}
                          {!isCurrent && (
                            <IconButton
                              size="small"
                              onClick={(e) => handleDeleteClick(item, e)}
                              sx={{
                                color: 'text.secondary',
                                '&:hover': {
                                  backgroundColor: 'error.light',
                                  color: 'white'
                                }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </AnimatePresence>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, backgroundColor: bg(theme).subtle, borderTop: `1px solid ${border(theme).default}` }}>
          <Button onClick={handleCloseHistory} variant="outlined" sx={{ borderRadius: 20, px: 3 }}>
            閉じる
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => !isDeleting && setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          履歴を削除しますか？
        </DialogTitle>

        <DialogContent>
          {itemToDelete && (
            <>
              <Typography variant="body1" gutterBottom>
                <strong>職種:</strong> {itemToDelete.position || itemToDelete.requirements?.position || '不明'}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>作成日:</strong> {new Date(itemToDelete.created_at).toLocaleDateString('ja-JP', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  timeZone: 'Asia/Tokyo'
                })}
              </Typography>

              <Alert severity="warning" sx={{ mt: 2 }}>
                削除した履歴は復元できません
              </Alert>

              {itemToDelete.is_decided && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  このデータは決定済みです。本当に削除しますか？
                </Alert>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            disabled={isDeleting}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {isDeleting ? '削除中...' : '削除する'}
          </Button>
        </DialogActions>
      </Dialog>


      {/* 変更通知用のSnackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="info"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </AppLayout>
  );
}